import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";

import {useTranslation} from "react-i18next";

import { enterExitStyle, CSSGrid, layout } from "react-stonecutter";

import { LinearProgress, styled, Grid, Card, CardHeader, CardContent } from "@mui/material";

import useDataSource from "../../../../hooks/useDataSource";
import PerformanceIndicator from "./PerformanceIndicator";
import ClearFilterBtn from "../../../application/ClearFilterBtn";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import {findComponent} from "../../../../redux/page/pageActions";

const PREFIX = 'KPIGroup';

const classes = {
	root: `${PREFIX}-root`,
	linearProgress: `${PREFIX}-linearProgress`,
	cardHeader: `${PREFIX}-cardHeader`,
	cardContent: `${PREFIX}-cardContent`
};

const Root = styled('div')((
	{
		theme
	}
) => ({
	[`&.${classes.root}`]: {
		height: 'calc(100% - 1px)'
	},

	[`& .${classes.linearProgress}`]: {
		height: '1px'
	},

	[`& .${classes.cardHeader}`]: {
		padding: '8px 8px 0px 8px',
		alignItems: 'start',
		textAlign: 'left',
		textTransform: 'none'
	},

	[`& .${classes.cardContent}`]: {
		flex: 'auto',
		height: '100%',
		minWidth: '315px',
		overflow: 'auto',
		zIndex: '50'
	}
}));

export default function KPIGroup({id, configuration, xId}) {

	const { enter, entered, exit } = enterExitStyle.simple;
	const dispatch = useDispatch();
	const { t } = useTranslation();

	// App State
	const app = useSelector(state => state.app);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

	// Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	const [attributeList, setAttributes] = useState([]);
	const [width, setWidth] = useState(window.innerWidth);
	const [cardColumns, setCardColumns] = useState(2);
	const [showProgress, setShowProgress] = useState(compConfig.element?.showFetching !== undefined ? compConfig.element?.showFetching : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});

	const gridCell = compConfig?.element?.gridCell;
	const styles = compConfig?.element?.styles;
	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource.name : '';

	const dataSource = useDataSource(dataSourceName);


	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// Titles & Resized
	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'KPI Group Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'KPIGroup'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

	// Data
	useEffect(() => {
		if (dataSource.data && dataSource.fetched && /*fields &&*/ dataSource.data.length > 0) {
			const compiledAttributesArray = [];

			for (let i = 0; i < dataSource.data.length; i++) {
				const pi = dataSource.data[i];

				compiledAttributesArray.push(
					<div key={pi[compConfig.dataSource.piCardKey]} itemHeight={175}>
						<PerformanceIndicator key={'pi{0}'.format(window.dw.utils.newID())}
											  id={'{0}-{1}'.format(compConfig.dataSource.name, compConfig.dataSource.fields.field.name)}
											  configuration={compConfig}
											  xId={null}
											  dataSourceIndex={i}/>
					</div>
				);

			}
			setAttributes(compiledAttributesArray);

		} else {
			setAttributes([]);

		}
	}, [dataSource.data]);

	useLayoutEffect(() => {
		let clientWidth = document.getElementById(id).clientWidth;
		setWidth(clientWidth);

		let columns = 0;
		if (width > 0) {
			const canvasWidth = width - (app.rightDrawerOpen ? app.rightDrawerWidth : 0);
			columns = canvasWidth / (gridCell.width + 20);
			columns = parseInt(columns, 0);
			if (columns < 1) {
				setCardColumns(1);
			} else {
				setCardColumns(columns);
			}
		}
	});

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent});

		if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

	return (
		<Root className={classes.root}>
			{showProgress && dataSource.fetching &&
				<LinearProgress className={classes.linearProgress}/>
			}
			{showProgress && !dataSource.fetching &&
				<div className={classes.linearProgress}/>
			}
			<Card style={{height: '100%', boxShadow: 'none'}}>
				<CardHeader action={ClearFilterBtn(id, false)} className={classes.cardHeader} style={cardHeaderStyle}
							titleTypographyProps={{variant: 'subtitle2', color: 'textPrimary'}}
							title={t(title)}
							subheaderTypographyProps={{variant: 'body2', color: 'textSecondary'}}
							subheader={t(subtitle)}>
				</CardHeader>
				<CardContent id={id} className={classes.cardContent} style={cardContentStyle}>
					<Grid container justifyContent={"center"} spacing={0}>
						<Grid item>
							<div style={{
								display: 'inline-block',
								minWidth: '320px'
							}}>
								<CSSGrid
									component="div"
									columns={cardColumns}
									columnWidth={gridCell.width}
									gutterWidth={5}
									gutterHeight={5}
									layout={layout.pinterest}
									duration={600}
									itemHeight={gridCell.height}
									enter={enter}
									entered={entered}
									exit={exit}
									// enter={() => ({ scale: 1, opacity: 1, rotate:-720 })}
									// entered={() => ({ scale: 1, opacity: 1, rotate:-720 })}
									// exit={() => ({ scale: 1, opacity: 1, rotate:-720 })}
									springConfig={{stiffness: 170, damping: 26}}
								>
									{attributeList}
								</CSSGrid>
							</div>
						</Grid>
					</Grid>
				</CardContent>
			</Card>

		</Root>
	);
}
